import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import _ from 'lodash'
import { getSpecificAttrValue } from 'utils'
import { getDeviceType, SMALL } from '@fil-global/gds-components/scripts/util/util'

import { getCustomIntl } from 'config/config'
import { Button } from 'gdpp-components/dist/cjs/components/atoms'
import { Table } from 'gdpp-components/dist/cjs/components/molecules'

import PageLevelMessage from '../../atoms/PageLevelMessage'
import GridContainer from '../../atoms/FoundationGrid/GridContainer'
import Grid from '../../atoms/FoundationGrid/Grid'
import Cell from '../../atoms/FoundationGrid/Cell'
import CustomHighchart from '../../molecules/CustomHighchart'

import './style.scss'

const FundDetailESGTab = ({ fundESGTable, esgChartData, esgMetrics, esgDownloadData, esgDisclosureData, children }) => {
  const intl = getCustomIntl()
  const [dataLabelEnabled, setDataLabelStatus] = useState(true)
  const { downloadLabel, downloadUrl } = esgDownloadData
  const { downloadLabel: esgDownloadLabel, downloadUrl: esgDownloadUrl } = esgDisclosureData

  const isMobile = () => {
    const breakpoint = getDeviceType()
    return breakpoint === SMALL
  }

  const handleResize = () => {
    setDataLabelStatus(!isMobile())
  }

  useEffect(() => {
    setDataLabelStatus(!isMobile())
    window.addEventListener('resize', handleResize)
  }, [])

  return (
    <GridContainer extraClasses="fil-fund-detail__tab-esg">
      {children || (
        <React.Fragment>
          <div className="fil-fund-detail__tab-esg__goal">
            <div id="fil-fund-detail__tab-esg__goal-title" className="text-heading-s fil-fund-detail__tab-esg__goal-title text-left">
              <FormattedMessage id="fundDetail.tabs.esgTab.esgGoalTitle" />
            </div>
            <div id="fil-fund-detail__tab-esg__goal-content" className="text-body-m fil-fund-detail__tab-esg__goal-content text-left">
              <span
                dangerouslySetInnerHTML={{
                  __html:
                    getSpecificAttrValue({ originData: esgMetrics, targetAttr: 'description', attrValue: 'zh-TW', valuePlaceholder: '' }) ||
                    getSpecificAttrValue({
                      originData: esgMetrics,
                      targetAttr: 'description',
                      attrValue: 'en-001',
                      valuePlaceholder: ''
                    })
                }}
              />
            </div>
          </div>
          <Grid extraClasses="fil-fund-detail__tab-esg__sustainability">
            <Cell
              id="fil-fund-detail__tab-esg__sustainability-title"
              extraClasses="text-heading-s fil-fund-detail__tab-esg__sustainability-title"
            >
              <Grid>
                <Cell extraClasses="large-auto">
                  <div>
                    <FormattedMessage id="fundDetail.tabs.esgTab.esgSustainabilityTitle" />
                  </div>
                </Cell>
                {downloadUrl && (
                  <Cell extraClasses="large-auto fil-fund-detail__tab-esg__sustainability-download">
                    <Button
                      kind="textlink"
                      icon="download-bottom"
                      iconPosition="left"
                      iconSize="16"
                      label={downloadLabel}
                      url={downloadUrl}
                      extraClasses="fil-fund-detail__tab-esg__sustainability-download-link"
                    />
                  </Cell>
                )}
              </Grid>
            </Cell>
            <Cell>
              {fundESGTable?.tableData ? (
                <Grid>
                  <Cell>
                    <Table
                      id="fil-fund-detail__fund-info-table"
                      {...fundESGTable.tableData}
                      disclaimer={
                        <span dangerouslySetInnerHTML={{ __html: intl.formatMessage({ id: 'fundDetail.tabs.esgTab.tableDisclaimer' }) }} />
                      }
                      size="small"
                      isAutoFitWidth
                      isScrollable={false}
                    />
                  </Cell>
                </Grid>
              ) : (
                _.isEmpty(esgDisclosureData) && (
                  <Grid extraClasses="align-center">
                    <Cell extraClasses="large-8">
                      <PageLevelMessage
                        id="fil-fund-detail__esg-tab-table-error-msg"
                        content={intl.formatMessage({ id: 'dataErrorMessage' })}
                        closable
                      />
                    </Cell>
                  </Grid>
                )
              )}
            </Cell>
          </Grid>
          <Grid extraClasses="fil-fund-detail__tab-esg__rating-distribution">
            <Cell
              id="fil-fund-detail__tab-esg__rating-distribution-title"
              extraClasses="text-heading-s fil-fund-detail__tab-esg__rating-distribution-title"
            >
              <Grid>
                <Cell extraClasses="large-auto">
                  <div>
                    <FormattedMessage id="fundDetail.tabs.esgTab.esgRatingDistributionTitle" />
                    {esgChartData?.effectiveDate && (
                      <span className="fil-fund-detail__tab-esg__rating-distribution-effective-date">({esgChartData.effectiveDate})</span>
                    )}
                  </div>
                </Cell>
                {esgDownloadUrl && (
                  <Cell extraClasses="large-auto fil-fund-detail__tab-esg__rating-distribution-download">
                    <Button
                      kind="textlink"
                      icon="download-bottom"
                      iconPosition="left"
                      iconSize="16"
                      label={esgDownloadLabel}
                      url={esgDownloadUrl}
                      extraClasses="fil-fund-detail__tab-esg__rating-distribution-download-link"
                    />
                  </Cell>
                )}
              </Grid>
            </Cell>

            <Cell>
              {esgChartData?.chartData ? (
                <Grid>
                  <Cell>
                    <CustomHighchart
                      id={`${esgChartData.scId}-esg-rating-distribution-chart`}
                      chartType="chart"
                      options={{
                        chart: {
                          type: 'column'
                        },
                        title: { text: '' },
                        xAxis: {
                          categories: esgChartData.categories,
                          crosshair: true
                        },
                        yAxis: {
                          title: { text: '' },
                          labels: {
                            formatter(yLabelFormatter) {
                              return `${parseInt(yLabelFormatter.value, 10)}%`
                            }
                          }
                        },
                        credits: {
                          enabled: false
                        },
                        tooltip: {
                          useHTML: true,
                          formatter() {
                            const self = this
                            return `<div>
                            <p>${self.point.series.name}</p>
                            <p>${self.point.y}%</p>
                          </div>`
                          }
                        },
                        plotOptions: {
                          column: {
                            pointPadding: 0.2,
                            maxPointWidth: 15,
                            minPointLength: 1,
                            dataLabels: {
                              enabled: dataLabelEnabled
                            }
                          }
                        },
                        series: esgChartData.chartData
                      }}
                    />
                  </Cell>
                </Grid>
              ) : (
                _.isEmpty(esgDisclosureData) && (
                  <Grid extraClasses="align-center">
                    <Cell extraClasses="large-8">
                      <PageLevelMessage
                        id="fil-fund-detail__esg-tab-table-error-msg"
                        content={intl.formatMessage({ id: 'dataErrorMessage' })}
                        closable
                      />
                    </Cell>
                  </Grid>
                )
              )}
            </Cell>
          </Grid>
          <Grid extraClasses="fil-fund-detail__tab-esg__footer">
            <div id="fil-fund-detail__tab-esg__footer-glossary" className="fil-fund-detail__tab-esg__footer-glossary">
              <div className="fil-fund-detail__tab-esg__footer-glossary-title">
                <FormattedMessage id="fundDetail.tabs.esgTab.esgGlossaryTitle" />
              </div>
              <div
                className="fil-fund-detail__tab-esg__footer-glossary-content"
                dangerouslySetInnerHTML={{ __html: intl.formatMessage({ id: 'fundDetail.tabs.esgTab.esgGlossaryContent' }) }}
              />
            </div>
            <div id="fil-fund-detail__tab-esg__footer-disclaimer" className="fil-fund-detail__tab-esg__footer-disclaimer">
              <div className="fil-fund-detail__tab-esg__footer-disclaimer-title">
                <FormattedMessage id="fundDetail.tabs.esgTab.esgDisclaimerTitle" />
              </div>
              <div
                className="fil-fund-detail__tab-esg__footer-glossary-content"
                dangerouslySetInnerHTML={{ __html: intl.formatMessage({ id: 'fundDetail.tabs.esgTab.esgDisclaimerContent' }) }}
              />
            </div>
          </Grid>
        </React.Fragment>
      )}
    </GridContainer>
  )
}

FundDetailESGTab.propTypes = {
  fundESGTable: PropTypes.object,
  children: PropTypes.any,
  esgChartData: PropTypes.object,
  esgMetrics: PropTypes.array,
  esgDownloadData: PropTypes.object,
  esgDisclosureData: PropTypes.object
}

FundDetailESGTab.defaultProps = {
  fundESGTable: {},
  esgChartData: {},
  esgMetrics: [],
  children: null,
  esgDownloadData: {},
  esgDisclosureData: {}
}

export default FundDetailESGTab
